import { useQuery } from '@apollo/client';
import { useMemo } from 'react';

import {
    GOVERNMENT_OFFER_PRIORITY,
    INSURANCE_OFFER_PRIORITY,
    type PriceSubOption,
    SELF_PAY_OFFER_PRIORITY,
    isGovernmentOfferType,
    isInsuranceOfferType,
    isSelfPayOfferType,
} from '@phx/data-sources/medication-marketplace';

import type { FillPathway } from '../contexts/fill';
import { getFragment } from '../graphql/client/helpers/get-fragment';
import type {
    CashOfferInfoFragment,
    InsuranceOfferInfoFragment,
    NonPriceableInsuranceOfferInfoFragment,
    NonPriceableOfferInfoFragment,
    ProviderOffersInfoFragment,
} from '../graphql/generated/graphql';
import { GetPrescriptionOffersDocument } from '../graphql/generated/graphql';

import type { HeadlinePrice } from './use-config-api';

export type PrescriptionOffer = {
    id: string;
    providerOffersId: string;
    headlinePriceType: PriceSubOption['priceType'] | null | undefined;
    patientCost: number | null;
    isInsurance: boolean;
    provider: ProviderOffersInfoFragment['provider'];
};

export const usePrescriptionOffers = (
    drxId: string | undefined,
    pathway: FillPathway | null
) => {
    const { data, loading } = useQuery(GetPrescriptionOffersDocument, {
        errorPolicy: 'all',
        variables: {
            drxId: drxId!,
            input: {},
        },
        skip: !drxId,
    });

    const order = data?.prescriberOrderV2;
    const offers = useMemo<PrescriptionOffer[]>(() => {
        const offerArr: PrescriptionOffer[] = [];

        if (order?.__typename === 'DigitallyReceivedPrescriberOrder') {
            const offersConnection = getFragment(order.offersConnection);

            offersConnection?.edges.forEach((edge) => {
                const node = getFragment(edge.node);
                const { provider } = node;

                node.offers.forEach((offer) => {
                    const offerFragment = getFragment(offer);
                    const headlinePriceType =
                        getHeadlinePriceType(offerFragment);

                    offerArr.push({
                        id: offerFragment.id,
                        providerOffersId: node.id,
                        headlinePriceType,
                        patientCost: getPatientCost(offerFragment),
                        isInsurance: isInsuranceOfferType(headlinePriceType),
                        provider,
                    });
                });
            });
        }

        return filterOffersByPathway(offerArr, pathway);
    }, [order, pathway]);

    return { offers, loading };
};

export const filterOffersByPathway = (
    offerArr: PrescriptionOffer[],
    pathway: FillPathway | null
): PrescriptionOffer[] => {
    switch (pathway) {
        case 'government':
            return offerArr.filter((offer) =>
                isGovernmentOfferType(offer.headlinePriceType)
            );
        case 'insurance':
            return offerArr.filter((offer) =>
                isInsuranceOfferType(offer.headlinePriceType)
            );
        case 'self-pay':
            return offerArr.filter((offer) =>
                isSelfPayOfferType(offer.headlinePriceType)
            );
        case 'fallback':
            return offerArr.filter(
                (offer) =>
                    offer.headlinePriceType === 'InsuredPriceAtPharmacy' ||
                    offer.headlinePriceType === 'SelfPayPriceAtPharmacy' ||
                    offer.headlinePriceType === 'PharmacyIncentive' ||
                    offer.headlinePriceType === 'DiscountedCashPrice'
            );
    }

    return offerArr;
};

// @TODO: move sub-pathways to a string union for better type safety later
export const filterOffersBySubPathway = (
    offerArr: PrescriptionOffer[],
    subPathway: string | null
): PrescriptionOffer[] => {
    if (!subPathway) {
        return offerArr;
    }

    switch (subPathway) {
        case 'medicare-bridge':
            return offerArr.filter(
                (offer) => offer.headlinePriceType === 'MedicareBridge'
            );
        case 'medicare-part-d':
            return offerArr.filter(
                (offer) => offer.headlinePriceType === 'MedicarePartD'
            );
        case 'other-government':
            return offerArr.filter(
                (offer) =>
                    offer.headlinePriceType === 'GovernmentPriceAtPharmacy'
            );
        case 'retail':
            return offerArr.filter(
                (offer) => offer.headlinePriceType === 'Retail'
            );
    }

    return offerArr;
};

export const getBestOffer = (
    offers: PrescriptionOffer[],
    priority: readonly HeadlinePrice['type'][]
): PrescriptionOffer | undefined => pickBest(offers, priority)[0];

const pickBest = (
    offers: PrescriptionOffer[],
    priority: readonly HeadlinePrice['type'][]
): PrescriptionOffer[] => {
    if (offers.length <= 1) {
        return offers;
    }

    return [
        offers.reduce((bestSoFar, offer) => {
            const bestIdx = priority.indexOf(
                bestSoFar.headlinePriceType as HeadlinePrice['type']
            );
            const offerIdx = priority.indexOf(
                offer.headlinePriceType as HeadlinePrice['type']
            );

            return offerIdx < bestIdx ? offer : bestSoFar;
        }),
    ];
};

export const deduplicateOffers = (
    offers: PrescriptionOffer[]
): PrescriptionOffer[] => {
    const byProvider = new Map<string, PrescriptionOffer[]>();

    for (const offer of offers) {
        const group = byProvider.get(offer.providerOffersId) ?? [];
        group.push(offer);
        byProvider.set(offer.providerOffersId, group);
    }

    const result: PrescriptionOffer[] = [];

    for (const group of byProvider.values()) {
        const selfPay = group.filter((o) =>
            isSelfPayOfferType(o.headlinePriceType)
        );
        const insurance = group.filter((o) =>
            isInsuranceOfferType(o.headlinePriceType)
        );
        const government = group.filter((o) =>
            isGovernmentOfferType(o.headlinePriceType)
        );
        const other = group.filter(
            (o) =>
                !isGovernmentOfferType(o.headlinePriceType) &&
                !isSelfPayOfferType(o.headlinePriceType) &&
                !isInsuranceOfferType(o.headlinePriceType)
        );

        result.push(
            ...pickBest(selfPay, SELF_PAY_OFFER_PRIORITY),
            ...pickBest(insurance, INSURANCE_OFFER_PRIORITY),
            ...pickBest(government, GOVERNMENT_OFFER_PRIORITY),
            ...other
        );
    }

    return result;
};

export const getHeadlinePriceType = (
    offer:
        | CashOfferInfoFragment
        | InsuranceOfferInfoFragment
        | NonPriceableOfferInfoFragment
        | NonPriceableInsuranceOfferInfoFragment
): PriceSubOption['priceType'] | null => {
    return (offer.headlinePriceType as PriceSubOption['priceType']) ?? null;
};

export const getPatientCost = (
    offer:
        | CashOfferInfoFragment
        | InsuranceOfferInfoFragment
        | NonPriceableOfferInfoFragment
        | NonPriceableInsuranceOfferInfoFragment
): number | null => {
    if (
        offer.__typename === 'CashOffer' ||
        offer.__typename === 'InsuranceOffer'
    ) {
        return offer.patientResponsibilityCost;
    }

    return null;
};
